import { useCallback, useState } from 'react'
import { ElementConverter } from './ElementConverter'
import { KonvaEventObject } from 'konva/lib/Node'
import { Group, Layer, Stage } from 'react-konva'
import {
  RoomComponent,
  BeamComponent,
  JoistComponent,
  DoorComponent,
  HeaderComponent,
  PostComponent,
  WallComponent,
  WindowComponent,
  ResizeState,
  ResizeMode,
  Point2D,
} from './Elements'
import { ElementsData } from '../../../models/Construction'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'

interface ElementRendererProps {
  elementsData: ElementsData | null
  scale: number
  highlightedType?: string | null
  hiddenTypes?: Set<string>
  selectedElement?: { element: any; type: string } | null
  thicknessMultiplier?: number
  isFlipped?: boolean
  onElementSelect?: (element: any, type: string) => void
  onElementDelete?: (elementId: string, type: string) => void
  onElementUpdate?: (
    elementId: string,
    elementType: string,
    updates: any,
  ) => void
}

export const ElementRenderer: React.FC<ElementRendererProps> = ({
  elementsData,
  scale,
  thicknessMultiplier = 1,
  selectedElement,
  onElementSelect,
  onElementDelete,
  onElementUpdate,
  highlightedType,
  isFlipped = false,
  hiddenTypes = new Set(),
}) => {
  const bounds = ElementConverter.calculateBounds(elementsData!)
  const stageWidth = Math.max(800, bounds.width * scale)
  const stageHeight = Math.max(800, bounds.height * scale)
  const [resizeState, setResizeState] = useState<ResizeState | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleElementSelect = useCallback(
    (element: any, type: string) => {
      onElementSelect?.(element, type)
    },
    [onElementSelect],
  )

  const handleElementDelete = useCallback(
    (elementId: string, type: string) => {
      // Find element type by searching through all element arrays
      let elementType = ''

      if (elementsData?.walls?.find((w) => w.id === elementId))
        elementType = 'wall'
      else if (elementsData?.beams?.find((b) => b.id === elementId))
        elementType = 'beam'
      else if (
        elementsData?.spans
          ?.flatMap((s) => s.joists)
          .find((j) => j.id === elementId)
      )
        elementType = 'joist'
      else if (elementsData?.rooms?.find((r) => r.id === elementId))
        elementType = 'room'
      else if (elementsData?.posts?.find((p) => p.id === elementId))
        elementType = 'post'
      else {
        // Check for windows and doors in walls
        for (const wall of elementsData?.walls || []) {
          if (wall.windows?.find((w) => w.id === elementId)) {
            elementType = 'window'
            break
          }
          if (wall.doors?.find((d) => d.id === elementId)) {
            elementType = 'door'
            break
          }
        }
      }

      onElementDelete?.(elementId, type)
    },
    [elementsData, onElementDelete],
  )

  const handleElementUpdate = useCallback(
    (elementId: string, elementType: string, updates: any) => {
      onElementUpdate?.(elementId, elementType, updates)
    },
    [elementsData, onElementUpdate],
  )

  const handleResizeStart = useCallback(
    (
      elementId: string,
      elementType: string,
      mode: ResizeMode,
      startPos: Point2D,
      bounds: any,
    ) => {
      let startElementState: any = null

      switch (elementType) {
        case 'wall':
          startElementState = elementsData?.walls?.find(
            (w) => w.id === elementId,
          )
          break
        case 'room':
          startElementState = elementsData?.rooms?.find(
            (r) => r.id === elementId,
          )
          break
        case 'beam':
          startElementState = elementsData?.beams?.find(
            (b) => b.id === elementId,
          )
          break
        case 'post':
          startElementState = elementsData?.posts?.find(
            (p) => p.id === elementId,
          )
          break
        case 'joist':
          startElementState = elementsData?.spans
            ?.flatMap((s) => s.joists)
            .find((j) => j.id === elementId)
          break
        case 'window':
          startElementState = elementsData?.walls
            ?.flatMap((w) => w.windows || [])
            .find((win) => win.id === elementId)
          break
        case 'door':
          startElementState = elementsData?.walls
            ?.flatMap((w) => w.doors || [])
            .find((d) => d.id === elementId)
          break
        case 'header':
          startElementState = elementsData?.walls
            ?.flatMap((w) => w.windows || [])
            .flatMap((win) => win.headers || [])
            .find((h) => h.id === elementId)
          break
      }

      if (startElementState) {
        setResizeState({
          elementId,
          elementType,
          mode,
          startPos,
          startElementState: JSON.parse(JSON.stringify(startElementState)),
          startBounds: bounds,
        })
        setIsDragging(true)
      } else {
        console.log(
          'Element state not found for resize:',
          elementId,
          elementType,
        )
      }
    },
    [elementsData],
  )

  const handleMouseMove = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (!resizeState || !isDragging) return

      const stage = e.target.getStage()
      const pointer = stage?.getPointerPosition()
      if (!pointer) return

      const deltaX = (pointer.x - resizeState.startPos.x) / scale
      const deltaY = (pointer.y - resizeState.startPos.y) / scale

      let updates: any = {}
      const { mode, startElementState } = resizeState

      // Get the rotation angle for the current element
      const angle = startElementState.rotation_z || startElementState.angle || 0
      const radians = (angle * Math.PI) / 180
      const cos = Math.cos(radians)
      const sin = Math.sin(radians)

      // Transform mouse delta from world to local coordinates
      const localDeltaX = deltaX * cos + deltaY * sin
      const localDeltaY = -deltaX * sin + deltaY * cos

      switch (mode) {
        case 'move':
          // Movement uses world coordinates directly
          if (resizeState.elementType === 'wall') {
            updates = {
              center_point: [
                startElementState.center_point[0] + deltaX,
                startElementState.center_point[1] + deltaY,
              ],
            }
          } else if (
            ['beam', 'post', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            updates = {
              center_point: {
                x: startElementState.center_point.x + deltaX,
                y: startElementState.center_point.y + deltaY,
              },
            }
          }
          break

        case 'e': // East (right) handle - extend right side
          if (
            ['wall', 'beam', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            // In local space, dragging right increases length
            const newLength = Math.max(
              10,
              startElementState.length + localDeltaX,
            )

            // Center moves by half the length change in world coordinates
            const centerDeltaX = (localDeltaX / 2) * cos
            const centerDeltaY = (localDeltaX / 2) * sin

            const newCenter = Array.isArray(startElementState.center_point)
              ? [
                  startElementState.center_point[0] + centerDeltaX,
                  startElementState.center_point[1] + centerDeltaY,
                ]
              : {
                  x: startElementState.center_point.x + centerDeltaX,
                  y: startElementState.center_point.y + centerDeltaY,
                }

            updates = {
              length: newLength,
              center_point: newCenter,
            }
          }
          break

        case 'w': // West (left) handle - extend left side
          if (
            ['wall', 'beam', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            // In local space, dragging left (negative) increases length from left
            const newLength = Math.max(
              10,
              startElementState.length - localDeltaX,
            )

            // Center moves opposite to east
            const centerDeltaX = (localDeltaX / 2) * cos
            const centerDeltaY = (localDeltaX / 2) * sin

            const newCenter = Array.isArray(startElementState.center_point)
              ? [
                  startElementState.center_point[0] + centerDeltaX,
                  startElementState.center_point[1] + centerDeltaY,
                ]
              : {
                  x: startElementState.center_point.x + centerDeltaX,
                  y: startElementState.center_point.y + centerDeltaY,
                }

            updates = {
              length: newLength,
              center_point: newCenter,
            }
          }
          break

        case 'n': // North (top) handle - extend top side
          if (resizeState.elementType === 'wall') {
            // For walls, thickness is the full width
            // Dragging up (negative localDeltaY) increases thickness from top
            const newThickness = Math.max(
              1,
              startElementState.thickness - localDeltaY,
            )

            // Center moves in the opposite direction (up)
            const centerDeltaX = -(localDeltaY / 2) * sin
            const centerDeltaY = (localDeltaY / 2) * cos

            updates = {
              thickness: newThickness,
              center_point: [
                startElementState.center_point[0] + centerDeltaX,
                startElementState.center_point[1] + centerDeltaY,
              ],
            }
          } else if (
            ['beam', 'joist', 'header'].includes(resizeState.elementType)
          ) {
            const widthProperty =
              resizeState.elementType === 'header' ? 'thickness' : 'width'
            // Dragging up (negative localDeltaY) increases width from top
            const newWidth = Math.max(
              1,
              startElementState[widthProperty] - localDeltaY,
            )

            // Center moves up
            const centerDeltaX = -(localDeltaY / 2) * sin
            const centerDeltaY = (localDeltaY / 2) * cos

            updates = {
              [widthProperty]: newWidth / 2,
              center_point: {
                x: startElementState.center_point.x + centerDeltaX,
                y: startElementState.center_point.y + centerDeltaY,
              },
            }
          }
          break

        case 's': // South (bottom) handle - extend bottom side
          if (resizeState.elementType === 'wall') {
            // Dragging down (positive localDeltaY) increases thickness from bottom
            const newThickness = Math.max(
              1,
              startElementState.thickness + localDeltaY,
            )

            // Center moves down
            const centerDeltaX = -(localDeltaY / 2) * sin
            const centerDeltaY = (localDeltaY / 2) * cos

            updates = {
              thickness: newThickness,
              center_point: [
                startElementState.center_point[0] + centerDeltaX,
                startElementState.center_point[1] + centerDeltaY,
              ],
            }
          } else if (
            ['beam', 'joist', 'header'].includes(resizeState.elementType)
          ) {
            const widthProperty =
              resizeState.elementType === 'header' ? 'thickness' : 'width'
            // Dragging down (positive localDeltaY) increases width from bottom
            const newWidth = Math.max(
              1,
              startElementState[widthProperty] + localDeltaY,
            )

            // Center moves down
            const centerDeltaX = -(localDeltaY / 2) * sin
            const centerDeltaY = (localDeltaY / 2) * cos

            updates = {
              [widthProperty]: newWidth / 2,
              center_point: {
                x: startElementState.center_point.x + centerDeltaX,
                y: startElementState.center_point.y + centerDeltaY,
              },
            }
          }
          break

        case 'ne': // Northeast corner
          if (
            ['wall', 'beam', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            // Increase length to the right, increase width upward
            const newLength = Math.max(
              10,
              startElementState.length + localDeltaX,
            )

            const widthProperty =
              resizeState.elementType === 'wall'
                ? 'thickness'
                : resizeState.elementType === 'header'
                  ? 'thickness'
                  : 'width'
            const newWidth = Math.max(
              1,
              startElementState[widthProperty] - localDeltaY,
            )

            // Calculate center movement
            const lengthCenterDeltaX = (localDeltaX / 2) * cos
            const lengthCenterDeltaY = (localDeltaX / 2) * sin
            const widthCenterDeltaX = -(localDeltaY / 2) * sin
            const widthCenterDeltaY = (localDeltaY / 2) * cos

            const newCenter = Array.isArray(startElementState.center_point)
              ? [
                  startElementState.center_point[0] +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  startElementState.center_point[1] +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                ]
              : {
                  x:
                    startElementState.center_point.x +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  y:
                    startElementState.center_point.y +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                }

            updates = {
              length: newLength,
              [widthProperty]: newWidth / 2,
              center_point: newCenter,
            }
          }
          break

        case 'nw': // Northwest corner
          if (
            ['wall', 'beam', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            // Increase length to the left, increase width upward
            const newLength = Math.max(
              10,
              startElementState.length - localDeltaX,
            )

            const widthProperty =
              resizeState.elementType === 'wall'
                ? 'thickness'
                : resizeState.elementType === 'header'
                  ? 'thickness'
                  : 'width'
            const newWidth = Math.max(
              1,
              startElementState[widthProperty] - localDeltaY,
            )

            // Calculate center movement
            const lengthCenterDeltaX = (localDeltaX / 2) * cos
            const lengthCenterDeltaY = (localDeltaX / 2) * sin
            const widthCenterDeltaX = -(localDeltaY / 2) * sin
            const widthCenterDeltaY = (localDeltaY / 2) * cos

            const newCenter = Array.isArray(startElementState.center_point)
              ? [
                  startElementState.center_point[0] +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  startElementState.center_point[1] +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                ]
              : {
                  x:
                    startElementState.center_point.x +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  y:
                    startElementState.center_point.y +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                }

            updates = {
              length: newLength,
              [widthProperty]: newWidth / 2,
              center_point: newCenter,
            }
          }
          break

        case 'se': // Southeast corner
          if (
            ['wall', 'beam', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            // Increase length to the right, increase width downward
            const newLength = Math.max(
              10,
              startElementState.length + localDeltaX,
            )

            const widthProperty =
              resizeState.elementType === 'wall'
                ? 'thickness'
                : resizeState.elementType === 'header'
                  ? 'thickness'
                  : 'width'
            const newWidth = Math.max(
              1,
              startElementState[widthProperty] + localDeltaY,
            )

            // Calculate center movement
            const lengthCenterDeltaX = (localDeltaX / 2) * cos
            const lengthCenterDeltaY = (localDeltaX / 2) * sin
            const widthCenterDeltaX = -(localDeltaY / 2) * sin
            const widthCenterDeltaY = (localDeltaY / 2) * cos

            const newCenter = Array.isArray(startElementState.center_point)
              ? [
                  startElementState.center_point[0] +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  startElementState.center_point[1] +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                ]
              : {
                  x:
                    startElementState.center_point.x +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  y:
                    startElementState.center_point.y +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                }

            updates = {
              length: newLength,
              [widthProperty]: newWidth / 2,
              center_point: newCenter,
            }
          }
          break

        case 'sw': // Southwest corner
          if (
            ['wall', 'beam', 'joist', 'header'].includes(
              resizeState.elementType,
            )
          ) {
            // Increase length to the left, increase width downward
            const newLength = Math.max(
              10,
              startElementState.length - localDeltaX,
            )

            const widthProperty =
              resizeState.elementType === 'wall'
                ? 'thickness'
                : resizeState.elementType === 'header'
                  ? 'thickness'
                  : 'width'
            const newWidth = Math.max(
              1,
              startElementState[widthProperty] + localDeltaY,
            )

            // Calculate center movement
            const lengthCenterDeltaX = (localDeltaX / 2) * cos
            const lengthCenterDeltaY = (localDeltaX / 2) * sin
            const widthCenterDeltaX = -(localDeltaY / 2) * sin
            const widthCenterDeltaY = (localDeltaY / 2) * cos

            const newCenter = Array.isArray(startElementState.center_point)
              ? [
                  startElementState.center_point[0] +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  startElementState.center_point[1] +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                ]
              : {
                  x:
                    startElementState.center_point.x +
                    lengthCenterDeltaX +
                    widthCenterDeltaX,
                  y:
                    startElementState.center_point.y +
                    lengthCenterDeltaY +
                    widthCenterDeltaY,
                }

            updates = {
              length: newLength,
              [widthProperty]: newWidth / 2,
              center_point: newCenter,
            }
          }
          break
      }

      if (Object.keys(updates).length > 0) {
        handleElementUpdate(
          resizeState.elementId,
          resizeState.elementType,
          updates,
        )
      }
    },
    [resizeState, isDragging, scale, handleElementUpdate],
  )

  const handleMouseUp = useCallback(() => {
    setResizeState(null)
    setIsDragging(false)
  }, [])

  const getElementStyle = (elementType: string) => {
    const isHighlighted = isElementHighlighted(elementType)

    return {
      opacity: highlightedType && !isHighlighted ? 0.3 : 1,
      filter: isHighlighted
        ? 'drop-shadow(0 0 8px rgba(255, 107, 107, 0.6))'
        : 'none',
    }
  }

  const isElementHighlighted = (elementType: string): boolean => {
    if (!highlightedType) return false

    const typeMapping: Record<string, string[]> = {
      walls: ['wall'],
      beams: ['beam'],
      joists: ['joist'],
      posts: ['post'],
      'windows-bay': ['bay'],
      'windows-sliding': ['sliding'],
      'windows-hopper': ['hopper'],
      doors: ['door'],
      'rooms-living': ['room'],
      'rooms-storage': ['room'],
      'rooms-utility': ['room'],
    }

    return (
      typeMapping[highlightedType]?.includes(elementType.toLowerCase()) || false
    )
  }

  const getHighlightStyle = (isHighlighted: boolean) => ({
    opacity: highlightedType && !isHighlighted ? 0.3 : 1,
    filter: isHighlighted
      ? 'drop-shadow(0 0 8px rgba(255, 107, 107, 0.6))'
      : 'none',
  })

  const createElementCallbacks = useCallback(
    <T extends any>(elementType: string) => ({
      onSelect: (element: T) => handleElementSelect(element, elementType),
      onDelete: (elementId: string) =>
        handleElementDelete(elementId, elementType),
      onUpdate: (elementId: string, elementType: string,updates: any) =>
        handleElementUpdate(elementId, elementType, updates),
      onResizeStart: handleResizeStart,
    }),
    [
      handleElementSelect,
      handleElementDelete,
      handleElementUpdate,
      handleResizeStart,
    ],
  )

  const selectedElementIds = useSelector(
    (state: RootState) => state.construction.selectedElementsIds,
  )
  const hiddenStoreyElements = useSelector(
    (state: RootState) => state.construction.hiddenStoreyElements,
  )

  return (
    <Stage
      width={stageWidth}
      height={stageHeight}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleMouseUp}
      className='border border-gray-300 bg-white'
    >
      <Layer>
        {/* Render Rooms */}
        {elementsData?.rooms?.map((room) => {
          const elementStyle = getElementStyle(room.type)
          if (elementStyle.opacity === 0) return null
          const isRoomSelected =
            selectedElement?.element.id === room.id &&
            selectedElement?.type === 'room'
          const isHovered = selectedElementIds.includes(room.id)
          return (
            <Group key={room.id} {...elementStyle}>
              <RoomComponent
                element={room}
                scale={scale}
                thicknessMultiplier={thicknessMultiplier}
                isSelected={isRoomSelected}
                isHovered={isHovered}
                onSelect={(element) => handleElementSelect(element, 'room')}
                onDelete={(element) => handleElementDelete(element, 'room')}
                onUpdate={handleElementUpdate}
                onResizeStart={handleResizeStart}
              />
            </Group>
          )
        })}

        {/* Render Joists */}
        {elementsData?.spans?.map((span) =>
          span.joists?.map((joist) => {
            const elementStyle = getElementStyle(joist.type)
            if (elementStyle.opacity === 0) return null
            const isJoistSelected =
              selectedElement?.element.id === joist.id &&
              selectedElement?.type === 'joist'
            const isHovered = selectedElementIds.includes(joist.id)
            const callbacks = createElementCallbacks<typeof joist>('joist')
            return (
              <Group key={joist.id} {...elementStyle}>
                <JoistComponent
                  element={joist}
                  scale={scale}
                  thicknessMultiplier={thicknessMultiplier}
                  isSelected={isJoistSelected}
                  isHovered={isHovered}
                  onSelect={callbacks.onSelect}
                  onDelete={callbacks.onDelete}
                  onUpdate={callbacks.onUpdate}
                  onResizeStart={callbacks.onResizeStart}
                />
              </Group>
            )
          }),
        )}

        {/* Render Posts */}
        {elementsData?.posts?.map((post) => {
          const isHighlighted = isElementHighlighted(post.type)
          const isPostSelected =
            selectedElement?.element.id === post.id &&
            selectedElement?.type === 'post'
          const isHovered = selectedElementIds.includes(post.id)
          const callbacks = createElementCallbacks<typeof post>('post')
          return (
            <Group key={post.id} {...getHighlightStyle(isHighlighted)}>
              <PostComponent
                element={post}
                scale={scale}
                thicknessMultiplier={thicknessMultiplier}
                isSelected={isPostSelected}
                isHovered={isHovered}
                onSelect={callbacks.onSelect}
                onDelete={callbacks.onDelete}
                onUpdate={callbacks.onUpdate}
                onResizeStart={callbacks.onResizeStart}
              />
            </Group>
          )
        })}

        {/* Render Walls with Windows and Doors */}
        {elementsData?.walls?.map((wall) => {
          const wallElementStyle = getElementStyle(wall.type!)
          const isWallSelected =
            selectedElement?.element.id === wall.id &&
            selectedElement?.type === 'wall'
          const isHovered = selectedElementIds.includes(wall.id)
          const isHidden = hiddenStoreyElements[wall.id]
          if (wallElementStyle.opacity === 0) return null
          return (
            <Group key={wall.id}>
              {/* Wall Component */}
              <Group {...wallElementStyle}>
                <WallComponent
                  element={wall}
                  scale={scale}
                  thicknessMultiplier={thicknessMultiplier}
                  isSelected={isWallSelected}
                  isHovered={isHovered}
                  isHidden={isHidden}
                  onSelect={(element) => handleElementSelect(element, 'wall')}
                  onDelete={(element) => handleElementDelete(element, 'wall')}
                  onUpdate={handleElementUpdate}
                  onResizeStart={handleResizeStart}
                />
              </Group>

              {/* Windows */}
              {/* {wall.windows?.map((window) => {
                const windowElementStyle = getElementStyle(window.type)
                if (windowElementStyle.opacity === 0) return null
                const isWindowSelected =
                  selectedElement?.element.id === window.id &&
                  selectedElement?.type === window.type
                const isHovered = selectedElementIds.includes(window.id)
                const isHidden = hiddenStoreyElements[window.id]
                return (
                  <Group key={window.id} {...windowElementStyle}>
                    <WindowComponent
                      wall={wall}
                      element={window}
                      scale={scale}
                      isHidden={isHidden}
                      thicknessMultiplier={thicknessMultiplier}
                      isSelected={isWindowSelected}
                      isHovered={isHovered}
                      onSelect={(element) =>
                        handleElementSelect(element, window.type)
                      }
                      onDelete={(element) =>
                        handleElementDelete(element, window.type)
                      }
                      onUpdate={handleElementUpdate}
                      onResizeStart={handleResizeStart}
                    />
                  </Group>
                )
              })} */}

              {/* Headers for Windows */}
              {/* {wall.windows?.flatMap((window) =>
                window.headers?.map((header) => {
                  const isHeaderSelected =
                    selectedElement?.element.id === header.id &&
                    selectedElement?.type === 'header'
                  const isHovered = selectedElementIds.includes(header.id)
                  const isHidden = hiddenStoreyElements[window.id]
                  const headerElementStyle = getElementStyle(header.type)
                  if (headerElementStyle.opacity === 0) return null
                  const callbacks =
                    createElementCallbacks<typeof header>('header')
                  return (
                    <Group key={header.id} {...headerElementStyle}>
                      <HeaderComponent
                        element={header}
                        scale={scale}
                        isHidden={isHidden}
                        thicknessMultiplier={thicknessMultiplier}
                        isSelected={isHeaderSelected}
                        isHovered={isHovered}
                        onSelect={callbacks.onSelect}
                        onDelete={callbacks.onDelete}
                        onUpdate={callbacks.onUpdate}
                        onResizeStart={callbacks.onResizeStart}
                      />
                    </Group>
                  )
                }),
              )} */}

              {/* Doors */}
              {wall.doors?.map((door) => {
                const doorElementStyle = getElementStyle(door.type)
                if (doorElementStyle.opacity === 0) return null
                const isDoorSelected =
                  selectedElement?.element.id === door.id &&
                  selectedElement?.type === 'door'
                const isHovered = selectedElementIds.includes(door.id)
                const isHidden = hiddenStoreyElements[door.id]
                return (
                  <Group key={door.id} {...doorElementStyle}>
                    <DoorComponent
                      wall={wall}
                      element={door}
                      isHidden={isHidden}
                      scale={scale}
                      thicknessMultiplier={thicknessMultiplier}
                      isSelected={isDoorSelected}
                      isHovered={isHovered}
                      onSelect={(element) =>
                        handleElementSelect(element, 'door')
                      }
                      onDelete={(element) =>
                        handleElementDelete(element, 'door')
                      }
                      onUpdate={handleElementUpdate}
                      onResizeStart={handleResizeStart}
                    />
                  </Group>
                )
              })}
            </Group>
          )
        })}

        {/* Render Windows */}
        {elementsData?.walls?.map((wall) => {
          return (
            <Group key={wall.id}>

              {/* Windows */}
              {wall.windows?.map((window) => {
                const windowElementStyle = getElementStyle(window.type)
                if (windowElementStyle.opacity === 0) return null
                const isWindowSelected =
                  selectedElement?.element.id === window.id &&
                  selectedElement?.type === window.type
                const isHovered = selectedElementIds.includes(window.id)
                const isHidden = hiddenStoreyElements[window.id]
                return (
                  <Group key={window.id} {...windowElementStyle}>
                    <WindowComponent
                      wall={wall}
                      element={window}
                      scale={scale}
                      isHidden={isHidden}
                      thicknessMultiplier={thicknessMultiplier}
                      isSelected={isWindowSelected}
                      isHovered={isHovered}
                      onSelect={(element) =>
                        handleElementSelect(element, window.type)
                      }
                      onDelete={(element) =>
                        handleElementDelete(element, window.type)
                      }
                      onUpdate={handleElementUpdate}
                      onResizeStart={handleResizeStart}
                    />
                  </Group>
                )
              })}

            </Group>
          )
        })}

        {/* Render Beams */}
        {elementsData?.beams?.map((beam) => {
          const elementStyle = getElementStyle(beam.type)
          if (elementStyle.opacity === 0) return null
          const isBeamSelected =
            selectedElement?.element.id === beam.id &&
            selectedElement?.type === 'beam'
          const isHovered = selectedElementIds.includes(beam.id)
          const callbacks = createElementCallbacks<typeof beam>('beam')
          return (
            <Group key={beam.id} {...elementStyle}>
              <BeamComponent
                element={beam}
                scale={scale}
                thicknessMultiplier={thicknessMultiplier}
                isSelected={isBeamSelected}
                isHovered={isHovered}
                onSelect={callbacks.onSelect}
                onDelete={callbacks.onDelete}
                onUpdate={callbacks.onUpdate}
                onResizeStart={callbacks.onResizeStart}
              />
            </Group>
          )
        })}
      </Layer>
    </Stage>
  )
}
